// PDFDocument.jsx
import React from "react";
import { Page, Text, View, Document, Image } from "@react-pdf/renderer";

import imagesBase64 from "../../assets/images";
import pdfStyles from "./pdfStyles";

const PDFDocument = ({
  entradaResumo,
  saidaResumo,
  nomeParticipante,
  programa,
  dataEntrada,
  dataSaida,
  mentorEntradaResumo,
  mentorSaidaResumo,
  // NOVOS PROPS:
  maiorPar,
  menorPar,
}) => {
  if (!entradaResumo || !saidaResumo) return null;

  // ---- Protagonismo (participante) ----
  const entradasVals = Object.values(entradaResumo.resultados || {}).map(Number).filter(Number.isFinite);
  const saidasVals   = Object.values(saidaResumo.resultados   || {}).map(Number).filter(Number.isFinite);

  const mediaEntrada = entradasVals.length
    ? entradasVals.reduce((a, b) => a + b, 0) / entradasVals.length
    : NaN;

  const mediaSaida = saidasVals.length
    ? saidasVals.reduce((a, b) => a + b, 0) / saidasVals.length
    : NaN;

  const ganhoPercentual =
    Number.isFinite(mediaEntrada) && mediaEntrada !== 0 && Number.isFinite(mediaSaida)
      ? ((mediaSaida - mediaEntrada) / mediaEntrada) * 100
      : 0;

  // helpers locais do doc
  const valorParaPercentual = (valor) => {
    const percent = Math.round((valor / 10) * 100);
    const arredondado = Math.round(percent / 5) * 5;
    return Math.max(0, Math.min(arredondado, 100));
  };

  const getGraphImageName = (tipo, cor, valor) => {
    const percent = valorParaPercentual(valor);
    if (percent === 0) return tipo === "semi" ? "semiGraph0" : "fullGraph0";
    return `${tipo}Graph${cor.charAt(0).toUpperCase() + cor.slice(1)}${percent}`;
  };

  const getGraphImage = (tipo, cor, valor) => {
    const nome = getGraphImageName(tipo, cor, valor);
    return imagesBase64[nome] || imagesBase64[`${tipo}Graph0`];
  };

  // ---- Liderança (flutuante) ----
  const mediaLiderancaEntrada = parseFloat(entradaResumo.mediaLideranca);
  const mediaLiderancaSaida = parseFloat(saidaResumo.mediaLideranca);
  const ganhoLideranca =
    Number.isFinite(mediaLiderancaSaida) && Number.isFinite(mediaLiderancaEntrada)
      ? mediaLiderancaSaida - mediaLiderancaEntrada
      : NaN;

  const percentualLideranca =
    Number.isFinite(mediaLiderancaEntrada) && mediaLiderancaEntrada !== 0 && Number.isFinite(ganhoLideranca)
      ? (ganhoLideranca / mediaLiderancaEntrada) * 100
      : 0;

  // ---- Subcategorias (participante) ----
  const calcularMediaSub = (subResultados, filtro) => {
    const valores = Object.entries(subResultados || {})
      .filter(([chave]) => chave.toLowerCase().includes(filtro))
      .map(([, valor]) => parseFloat(valor))
      .filter(Number.isFinite);
    return valores.length
      ? valores.reduce((acc, val) => acc + val, 0) / valores.length
      : NaN;
  };

  const comunicacaoEntrada = calcularMediaSub(entradaResumo.subResultados, "comunicação");
  const comunicacaoSaida = calcularMediaSub(saidaResumo.subResultados, "comunicação");
  const autonomiaEntrada = calcularMediaSub(entradaResumo.subResultados, "autonomia");
  const autonomiaSaida = calcularMediaSub(saidaResumo.subResultados, "autonomia");

  const gerarBlocoSubcategoria = (entrada, saida, titulo) => {
    if (!Number.isFinite(entrada) || !Number.isFinite(saida)) return null;
    const ganhoBruto = saida - entrada;
    const percentual = entrada !== 0 ? (ganhoBruto / entrada) * 100 : 0;

    return (
      <View style={pdfStyles.indicatorBox}>
        <View style={pdfStyles.indicatorHeader}>
          <Text style={pdfStyles.graphDelta}>
            {percentual >= 0 ? "+" : "-"}
            {Math.abs(percentual).toFixed(0)}%
          </Text>
          <Text style={pdfStyles.graphPipe}>|</Text>
          <Text style={pdfStyles.graphDeltaRaw}>
            {ganhoBruto >= 0 ? "+" : "-"}
            {Math.abs(ganhoBruto).toFixed(2)}
          </Text>
        </View>

        <View style={pdfStyles.indicatorCircles}>
          <View style={pdfStyles.circleGraphWrapper}>
            <Image src={getGraphImage("full", "pink", entrada)} style={pdfStyles.circleGraph} />
            <Text style={pdfStyles.circleValue}>{entrada.toFixed(1)}</Text>
            <Image src={imagesBase64.arrowDown} style={pdfStyles.circleArrow} />
          </View>
          <View style={pdfStyles.circleGraphWrapper}>
            <Image src={getGraphImage("full", "green", saida)} style={pdfStyles.circleGraph} />
            <Text style={pdfStyles.circleValue}>{saida.toFixed(1)}</Text>
            <Image src={imagesBase64.arrowUp} style={pdfStyles.circleArrow} />
          </View>
        </View>

        <View style={pdfStyles.indicatorLabelWrapper}>
          <Text style={pdfStyles.indicatorLabelSub}>{titulo}</Text>
        </View>
        <Text style={[pdfStyles.indicatorCategory, { color: "#e91e63" }]}>score</Text>
      </View>
    );
  };

  // =========================
  //        MENTOR
  // =========================
  const temMentor =
    mentorEntradaResumo &&
    mentorSaidaResumo &&
    mentorEntradaResumo.resultados &&
    mentorSaidaResumo.resultados;

  // médias gerais do mentor
  const mentorEntVals = temMentor ? Object.values(mentorEntradaResumo.resultados || {}).map(Number).filter(Number.isFinite) : [];
  const mentorSaiVals = temMentor ? Object.values(mentorSaidaResumo.resultados   || {}).map(Number).filter(Number.isFinite) : [];

  const mentorMediaEntrada = mentorEntVals.length
    ? mentorEntVals.reduce((a, b) => a + b, 0) / mentorEntVals.length
    : NaN;

  const mentorMediaSaida = mentorSaiVals.length
    ? mentorSaiVals.reduce((a, b) => a + b, 0) / mentorSaiVals.length
    : NaN;

  // “Mentor Score” = média entre as médias (entrada/saída) do mentor
  const mentorScoreMedio =
    Number.isFinite(mentorMediaEntrada) && Number.isFinite(mentorMediaSaida)
      ? (mentorMediaEntrada + mentorMediaSaida) / 2
      : NaN;

  // MINI-BARRAS (categorias)
  const renderMiniBarMentorEntrada = (categoria) => {
    if (!temMentor) return null;
    const vEnt = parseFloat(mentorEntradaResumo.resultados[categoria]);
    if (isNaN(vEnt)) return null;
    return (
      <View style={pdfStyles.miniBarsWrapper}>
        <View style={pdfStyles.miniRow}>
          <View style={pdfStyles.miniBarTrack}>
            <View style={[pdfStyles.miniBarFillEntrada, { width: `${(vEnt / 10) * 100}%` }]} />
          </View>
          <Text style={pdfStyles.miniValueText}>{vEnt.toFixed(1)}</Text>
        </View>
      </View>
    );
  };

  const renderMiniBarMentorSaida = (categoria) => {
    if (!temMentor) return null;
    const vSai = parseFloat(mentorSaidaResumo.resultados[categoria]);
    if (isNaN(vSai)) return null;
    return (
      <View style={pdfStyles.miniBarsWrapper}>
        <View style={pdfStyles.miniRow}>
          <View style={pdfStyles.miniBarTrack}>
            <View style={[pdfStyles.miniBarFillSaida, { width: `${(vSai / 10) * 100}%` }]} />
          </View>
          <Text style={pdfStyles.miniValueText}>{vSai.toFixed(1)}</Text>
        </View>
      </View>
    );
  };

  // MINI-BARRAS (subcategorias)
  const renderMiniBarMentorEntradaSub = (chave, isFirst, isLast) => {
    if (!temMentor || !mentorEntradaResumo.subResultados) return null;
    const vEnt = parseFloat(mentorEntradaResumo.subResultados[chave]);
    if (isNaN(vEnt)) return null;
    return (
      <View
        style={[
          pdfStyles.miniBarsWrapperSub,
          isFirst && pdfStyles.miniBarsWrapperSubFirst,
          isLast && pdfStyles.miniBarsWrapperSubLast,
        ]}
      >
        <View style={pdfStyles.miniRowSub}>
          <View style={[pdfStyles.miniBarTrackSub, { width: 88 }]}>
            <View
              style={[pdfStyles.miniBarFillEntradaSub, { width: `${(vEnt / 10) * 100}%`, height: 3 }]}
            />
          </View>
          <Text style={pdfStyles.miniValueTextSub}>{vEnt.toFixed(1)}</Text>
        </View>
      </View>
    );
  };

  const renderMiniBarMentorSaidaSub = (chave, isFirst, isLast) => {
    if (!temMentor || !mentorSaidaResumo.subResultados) return null;
    const vSai = parseFloat(mentorSaidaResumo.subResultados[chave]);
    if (isNaN(vSai)) return null;
    return (
      <View
        style={[
          pdfStyles.miniBarsWrapperSub,
          isFirst && pdfStyles.miniBarsWrapperSubFirst,
          isLast && pdfStyles.miniBarsWrapperSubLast,
        ]}
      >
        <View style={pdfStyles.miniRowSub}>
          <View style={[pdfStyles.miniBarTrackSub, { width: 88 }]}>
            <View
              style={[pdfStyles.miniBarFillSaidaSub, { width: `${(vSai / 10) * 100}%`, height: 3 }]}
            />
          </View>
          <Text style={pdfStyles.miniValueTextSub}>{vSai.toFixed(1)}</Text>
        </View>
      </View>
    );
  };

  // barra de subcategoria (linha)
  const gerarSubcategoriaBar = (chave, idx, total) => {
    const valorEntrada = parseFloat(entradaResumo.subResultados[chave]);
    const valorSaida = parseFloat(saidaResumo.subResultados[chave]);
    const icone =
      valorSaida > valorEntrada
        ? imagesBase64.arrowUp
        : valorSaida < valorEntrada
        ? imagesBase64.arrowDown
        : null;

    const [categoria, sub] = chave.split(" - ");
    const isFirst = idx === 0;
    const isLast = idx === total - 1;

    return (
      <View
        key={`sub-${chave}`}
        style={[
          pdfStyles.subBarRow,
          isFirst && pdfStyles.firstSubRow,
          isLast && pdfStyles.lastSubRow,
        ]}
      >
        <View style={pdfStyles.subLabelGroup}>
          <Text style={pdfStyles.subTitleLabel}>{sub}</Text>
          <Text style={pdfStyles.subCategoryLabel}>{categoria}</Text>
        </View>

        <View style={pdfStyles.subBarStack}>
          {/* MINI ENTRADA (mentor) ACIMA */}
          {renderMiniBarMentorEntradaSub(chave, isFirst, isLast)}

          {/* BARRAS PARTICIPANTE */}
          <View style={pdfStyles.subDivBars}>
            <View style={pdfStyles.subDivBarsAux}>
              <View style={pdfStyles.subBarTrack}>
                <View
                  style={[pdfStyles.subBarFillEntrada, { width: `${(valorEntrada / 10) * 100}%` }]}
                />
              </View>
              <Text style={pdfStyles.subBarValue}>{valorEntrada.toFixed(1)}</Text>
            </View>

            <View style={pdfStyles.subDivBarsAux}>
              <View style={pdfStyles.subBarTrack}>
                <View
                  style={[pdfStyles.subBarFillSaida, { width: `${(valorSaida / 10) * 100}%` }]}
                />
              </View>
              <Text style={pdfStyles.subBarValue}>{valorSaida.toFixed(1)}</Text>
            </View>
          </View>

          {/* MINI SAÍDA (mentor) ABAIXO */}
          {renderMiniBarMentorSaidaSub(chave, isFirst, isLast)}
        </View>

        {icone && <Image src={icone} style={{ width: 10, height: 10, marginLeft: 12 }} />}
      </View>
    );
  };

  // lista combinada de chaves de subcategoria (para saber 1º/último)
  const subKeys = [
    ...Object.keys(entradaResumo.subResultados || {}).filter((k) =>
      k.toLowerCase().includes("comunicação")
    ),
    ...Object.keys(entradaResumo.subResultados || {}).filter((k) =>
      k.toLowerCase().includes("autonomia")
    ),
  ];

  // ===== helper para normalizar maior/menor vindos do pai =====
  // Usa Saída quando existir; se não existir, usa Entrada.
  const pairDisplay = (p) => {
    if (!p) return { entrada: NaN, saida: NaN, categoria: "" };
    const e0 = Number(p.entrada);
    const s0 = Number(p.saida);
    const e = Number.isFinite(e0) ? e0 : (Number.isFinite(s0) ? s0 : NaN);
    const s = Number.isFinite(s0) ? s0 : (Number.isFinite(e0) ? e0 : NaN);
    return { entrada: e, saida: s, categoria: p.categoria || "" };
  };

  return (
    <Document>
      {/* Página 1 */}
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.upBar} />
        <View style={pdfStyles.mainDiv}>
          <View style={pdfStyles.divTitle}>
            <Image src={imagesBase64.logo} style={pdfStyles.image} />
            <View style={pdfStyles.divTitleAux}>
              <Text style={pdfStyles.title}>Os 5 P's do Protagonismo</Text>
              <Text style={pdfStyles.subtitle}>
                {`São Paulo, ${new Date().toLocaleDateString("pt-BR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}`}
              </Text>
            </View>
          </View>

          <View style={pdfStyles.divMid}>
            <View style={pdfStyles.divMidInner}>
              <View style={pdfStyles.barSection}>
                {Object.keys(entradaResumo.resultados).map((categoria, idx, arr) => {
                  const valorEntrada = parseFloat(entradaResumo.resultados[categoria]);
                  const valorSaida = parseFloat(saidaResumo.resultados[categoria]);
                  const icone =
                    valorSaida > valorEntrada
                      ? imagesBase64.arrowUp
                      : valorSaida < valorEntrada
                      ? imagesBase64.arrowDown
                      : null;

                  const rowStyle = [
                    pdfStyles.barRow,
                    idx === 0 && pdfStyles.firstRow,
                    idx === arr.length - 1 && pdfStyles.lastRow,
                  ];

                  return (
                    <View key={categoria} style={rowStyle}>
                      <Text style={pdfStyles.barLabel}>{categoria}</Text>

                      <View style={pdfStyles.barStack}>
                        {renderMiniBarMentorEntrada(categoria)}

                        <View style={pdfStyles.divBars}>
                          <View style={pdfStyles.divBarsAux}>
                            <View style={pdfStyles.barTrack}>
                              <View
                                style={[
                                  pdfStyles.barFillEntrada,
                                  { width: `${(valorEntrada / 10) * 100}%` },
                                ]}
                              />
                            </View>
                            <Text style={pdfStyles.barValue}>{valorEntrada.toFixed(1)}</Text>
                          </View>

                          <View style={pdfStyles.divBarsAux}>
                            <View style={pdfStyles.barTrack}>
                              <View
                                style={[
                                  pdfStyles.barFillSaida,
                                  { width: `${(valorSaida / 10) * 100}%` },
                                ]}
                              />
                            </View>
                            <Text style={pdfStyles.barValue}>{valorSaida.toFixed(1)}</Text>
                          </View>
                        </View>

                        {renderMiniBarMentorSaida(categoria)}
                      </View>

                      {icone && (
                        <Image src={icone} style={{ width: 10, height: 10, marginLeft: 12 }} />
                      )}
                    </View>
                  );
                })}
              </View>

              <View style={pdfStyles.scoreBox}>
                {/* Protagonismo Score (participante) */}
                <View style={pdfStyles.upperGraphs}>
                  <View style={pdfStyles.upperGraphsTopRow}>
                    <Text style={pdfStyles.graphDelta}>
                      {ganhoPercentual >= 0 ? "+" : "-"}
                      {Math.abs(ganhoPercentual).toFixed(1)}%
                    </Text>
                    <Text style={pdfStyles.graphPipe}>|</Text>
                    <Text style={pdfStyles.graphDeltaRaw}>
                      {Number.isFinite(mediaEntrada) && Number.isFinite(mediaSaida)
                        ? (mediaSaida - mediaEntrada).toFixed(2)
                        : "0.00"}
                    </Text>
                  </View>

                  <View style={pdfStyles.upperGraphsCircles}>
                    {Number.isFinite(mediaEntrada) && (
                      <View style={pdfStyles.graphWithValue}>
                        <Image
                          src={getGraphImage("semi", "pink", mediaEntrada)}
                          style={pdfStyles.semiGraph}
                        />
                        <Text style={pdfStyles.graphValue}>{mediaEntrada.toFixed(1)}</Text>
                      </View>
                    )}

                    {Number.isFinite(mediaSaida) && (
                      <View style={pdfStyles.graphWithValue}>
                        <Image
                          src={getGraphImage("semi", "green", mediaSaida)}
                          style={pdfStyles.semiGraph}
                        />
                        <Text style={pdfStyles.graphValue}>{mediaSaida.toFixed(1)}</Text>
                      </View>
                    )}
                  </View>

                  <View style={pdfStyles.graphLine} />
                  <Text style={pdfStyles.scoreTitle}>Protagonismo Score</Text>
                </View>

                {/* Mentor Score */}
                {temMentor && Number.isFinite(mentorScoreMedio) && (
                  <View style={pdfStyles.mentorGraphs}>
                    <View style={pdfStyles.mentorSingleCircles}>
                      <View style={pdfStyles.mentorSingleWrapper}>
                        <Image
                          src={getGraphImage("semi", "green", mentorScoreMedio)}
                          style={pdfStyles.semiGraphMentor}
                        />
                        <Text style={pdfStyles.graphMentorValue}>
                          {mentorScoreMedio.toFixed(1)}
                        </Text>
                      </View>
                    </View>

                    <View style={pdfStyles.graphLine} />
                    <Text style={pdfStyles.scoreTitle}>Mentor Score</Text>
                  </View>
                )}

                {/* Menor & Maior indicadores (usando os pares vindos por props) */}
                <View style={pdfStyles.bottomGraphs}>
                  {/* Menor indicador */}
                  <View style={pdfStyles.indicatorBox}>
                    {(() => {
                      const { entrada, saida, categoria } = pairDisplay(menorPar);
                      if (!Number.isFinite(entrada) || !Number.isFinite(saida)) return null;

                      const ganhoBruto = saida - entrada;
                      const percentual = entrada !== 0 ? (ganhoBruto / entrada) * 100 : 0;

                      return (
                        <>
                          <View style={pdfStyles.indicatorHeader}>
                            <Text style={pdfStyles.graphDelta}>
                              {percentual >= 0 ? "+" : "-"}
                              {Math.abs(percentual).toFixed(0)}%
                            </Text>
                            <Text style={pdfStyles.graphPipe}>|</Text>
                            <Text style={pdfStyles.graphDeltaRaw}>
                              {ganhoBruto >= 0 ? "+" : "-"}
                              {Math.abs(ganhoBruto).toFixed(2)}
                            </Text>
                          </View>

                          <View style={pdfStyles.indicatorCircles}>
                            <View style={pdfStyles.circleGraphWrapper}>
                              <Image
                                src={getGraphImage("full", "pink", entrada)}
                                style={pdfStyles.circleGraph}
                              />
                              <Text style={pdfStyles.circleValue}>{entrada.toFixed(1)}</Text>
                              <Image src={imagesBase64.arrowDown} style={pdfStyles.circleArrow} />
                            </View>

                            <View style={pdfStyles.circleGraphWrapper}>
                              <Image
                                src={getGraphImage("full", "green", saida)}
                                style={pdfStyles.circleGraph}
                              />
                              <Text style={pdfStyles.circleValue}>{saida.toFixed(1)}</Text>
                              <Image src={imagesBase64.arrowUp} style={pdfStyles.circleArrow} />
                            </View>
                          </View>

                          <View style={pdfStyles.indicatorLine} />

                          <View style={pdfStyles.indicatorLabelWrapper}>
                            <Image src={imagesBase64.arrowDown} style={pdfStyles.arrowLabelIcon} />
                            <Text style={pdfStyles.indicatorLabel}>Menor indicador</Text>
                          </View>
                          <Text style={[pdfStyles.indicatorCategory, { color: "#e91e63" }]}>
                            {categoria}
                          </Text>
                        </>
                      );
                    })()}
                  </View>

                  {/* Maior indicador */}
                  <View style={pdfStyles.indicatorBox}>
                    {(() => {
                      const { entrada, saida, categoria } = pairDisplay(maiorPar);
                      if (!Number.isFinite(entrada) || !Number.isFinite(saida)) return null;

                      const ganhoBruto = saida - entrada;
                      const percentual = entrada !== 0 ? (ganhoBruto / entrada) * 100 : 0;

                      return (
                        <>
                          <View style={pdfStyles.indicatorHeader}>
                            <Text style={pdfStyles.graphDelta}>
                              {percentual >= 0 ? "+" : "-"}
                              {Math.abs(percentual).toFixed(0)}%
                            </Text>
                            <Text style={pdfStyles.graphPipe}>|</Text>
                            <Text style={pdfStyles.graphDeltaRaw}>
                              {ganhoBruto >= 0 ? "+" : "-"}
                              {Math.abs(ganhoBruto).toFixed(2)}
                            </Text>
                          </View>

                          <View style={pdfStyles.indicatorCircles}>
                            <View style={pdfStyles.circleGraphWrapper}>
                              <Image
                                src={getGraphImage("full", "pink", entrada)}
                                style={pdfStyles.circleGraph}
                              />
                              <Text style={pdfStyles.circleValue}>{entrada.toFixed(1)}</Text>
                              <Image src={imagesBase64.arrowDown} style={pdfStyles.circleArrow} />
                            </View>

                            <View style={pdfStyles.circleGraphWrapper}>
                              <Image
                                src={getGraphImage("full", "green", saida)}
                                style={pdfStyles.circleGraph}
                              />
                              <Text style={pdfStyles.circleValue}>{saida.toFixed(1)}</Text>
                              <Image src={imagesBase64.arrowUp} style={pdfStyles.circleArrow} />
                            </View>
                          </View>

                          <View style={pdfStyles.indicatorLine} />

                          <View style={pdfStyles.indicatorLabelWrapper}>
                            <Image src={imagesBase64.arrowUp} style={pdfStyles.arrowLabelIcon} />
                            <Text style={pdfStyles.indicatorLabel}>Maior indicador</Text>
                          </View>
                          <Text style={[pdfStyles.indicatorCategory, { color: "#009688" }]}>
                            {categoria}
                          </Text>
                        </>
                      );
                    })()}
                  </View>
                </View>
              </View>

              <View style={pdfStyles.userDataBox}>
                <View>
                  <Text style={pdfStyles.userDataLabel}>Participante:</Text>
                  <Text style={pdfStyles.userDataTitle}>{nomeParticipante}</Text>
                </View>

                <View>
                  <Text style={pdfStyles.userDataLabel}>Programa:</Text>
                  <Text style={pdfStyles.userDataTitle}>{programa}</Text>
                </View>

                <View style={pdfStyles.legendBox}>
                  <View style={pdfStyles.legendItem}>
                    <View style={[pdfStyles.legendBar, { backgroundColor: "#e91d75" }]}>
                      <Text style={pdfStyles.legendText}>Entrada</Text>
                    </View>
                    <Text style={pdfStyles.legendDate}>{dataEntrada}</Text>
                  </View>

                  <View style={pdfStyles.legendItem}>
                    <View style={[pdfStyles.legendBar, { backgroundColor: "#00afa9" }]}>
                      <Text style={pdfStyles.legendText}>Saída</Text>
                    </View>
                    <Text style={pdfStyles.legendDate}>{dataSaida}</Text>
                  </View>
                </View>
              </View>
            </View>

            <View style={pdfStyles.divTextVertical}>
              <Text style={pdfStyles.verticalText}>Categorias</Text>
            </View>
          </View>

          {/* Bloco explicativo + subcategorias */}
          <View style={pdfStyles.divBottom}>
            <View style={pdfStyles.aboutBox}>
              <View style={pdfStyles.aboutTitleRow}>
                <Image src={imagesBase64.sideImage} style={pdfStyles.aboutIcon} />
                <Text style={pdfStyles.aboutTitle}>Sobre os cinco P's</Text>
              </View>

              <Text style={pdfStyles.aboutText}>
                O protagonismo na vida é alcançado ao integrar cinco pilares fundamentais, conhecidos
                como os 5 P's:
                {"\n"}• <Text style={{ fontWeight: "bold" }}>Perfil</Text>, que enfatiza o
                autoconhecimento como base para decisões mais assertivas;
                {"\n"}• <Text style={{ fontWeight: "bold" }}>Pessoas</Text>, que destacam a
                importância de relações interpessoais saudáveis e influentes;
                {"\n"}• <Text style={{ fontWeight: "bold" }}>Processos</Text>, voltados à busca por
                eficiência e simplicidade no trabalho;
                {"\n"}• <Text style={{ fontWeight: "bold" }}>Produtividade</Text>, focada na gestão
                eficaz do tempo e na priorização de tarefas;
                {"\n"}• <Text style={{ fontWeight: "bold" }}>Propósito</Text>, que confere
                significado e paixão às atividades diárias.
                {"\n\n"}
                <Text style={{ fontWeight: "bold" }}>
                  Ao alinhar esses pilares, você assume o controle do seu desenvolvimento pessoal e
                  profissional, construindo uma trajetória coerente com seus valores e objetivos.
                </Text>
              </Text>
            </View>

            <View style={pdfStyles.subDiv}>
              <View style={pdfStyles.subBarSection}>
                {subKeys.map((chave, idx) => gerarSubcategoriaBar(chave, idx, subKeys.length))}
              </View>

              <View style={pdfStyles.subCircleSection}>
                {Number.isFinite(comunicacaoEntrada) && Number.isFinite(comunicacaoSaida) &&
                  gerarBlocoSubcategoria(comunicacaoEntrada, comunicacaoSaida, "Comunicação")}
                {Number.isFinite(autonomiaEntrada) && Number.isFinite(autonomiaSaida) &&
                  gerarBlocoSubcategoria(autonomiaEntrada, autonomiaSaida, "Autonomia")}
              </View>
            </View>

            <View style={[pdfStyles.aboutBox, { marginTop: 0 }]}>
              <View style={pdfStyles.aboutTitleRow}>
                <Image src={imagesBase64.sideImage} style={pdfStyles.aboutIcon} />
                <Text style={pdfStyles.aboutTitle}>Sobre as subcategorias</Text>
              </View>

              <Text style={pdfStyles.aboutText}>
                No contexto dos 5 P's do protagonismo, comunicação e autonomia são elementos
                transversais que potencializam cada pilar. A comunicação eficaz é essencial para o
                autoconhecimento (<Text style={{ fontWeight: "bold" }}>Perfil</Text>), para liderar e
                influenciar relacionamentos (<Text style={{ fontWeight: "bold" }}>Pessoas</Text>) e
                para otimizar rotinas e sistemas (
                <Text style={{ fontWeight: "bold" }}>Processos</Text>). Já a autonomia capacita o
                indivíduo a gerir seu tempo com eficiência (
                <Text style={{ fontWeight: "bold" }}>Produtividade</Text>) e a alinhar suas ações
                com um propósito significativo (
                <Text style={{ fontWeight: "bold" }}>Propósito</Text>).
                {"\n"}
                Ao considerar esses dois indicadores, temos uma visão mais integrada do
                desenvolvimento pessoal e profissional, evidenciando como a comunicação e a
                autonomia são fundamentais para assumir o controle da própria trajetória.
              </Text>
            </View>

            <View style={pdfStyles.divTextVerticalBottom}>
              <Text style={pdfStyles.verticalText}>SubCategorias</Text>
            </View>
          </View>

          <View style={pdfStyles.noteSection}>
            <View style={pdfStyles.noteBar} />
            <Text style={pdfStyles.noteText}>
              Proibida a reprodução total ou parcial deste relatório, seja qual for o meio,
              eletrônico ou impresso, sem a permissão expressa do{" "}
              <Text style={pdfStyles.noteBold}>Método Santé.</Text>
            </Text>
          </View>

          <Image src={imagesBase64.sideImage} style={pdfStyles.sideDecoration} />
        </View>

        {/* Gráfico flutuante de liderança */}
        {Number.isFinite(mediaLiderancaEntrada) && Number.isFinite(mediaLiderancaSaida) && (
          <View style={pdfStyles.floatingLiderancaBox}>
            <View style={pdfStyles.floatingInnerLiderancaBox}>
              <View style={pdfStyles.floatingGraphsTopRow}>
                <Text style={pdfStyles.graphDelta}>
                  {percentualLideranca >= 0 ? "+" : "-"}
                  {Math.abs(percentualLideranca).toFixed(1)}%
                </Text>
                <Text style={pdfStyles.graphPipe}>|</Text>
                <Text style={pdfStyles.graphDeltaRaw}>
                  {Number.isFinite(ganhoLideranca)
                    ? `${ganhoLideranca >= 0 ? "+" : "-"}${Math.abs(ganhoLideranca).toFixed(2)}`
                    : "+0.00"}
                </Text>
              </View>

              <View style={pdfStyles.floatingGraphsCircles}>
                <View style={pdfStyles.graphWithValue}>
                  <Image
                    src={getGraphImage("semi", "pink", mediaLiderancaEntrada)}
                    style={pdfStyles.semiGraph}
                  />
                  <Text style={pdfStyles.graphValue}>{mediaLiderancaEntrada.toFixed(1)}</Text>
                </View>

                <View style={pdfStyles.graphWithValue}>
                  <Image
                    src={getGraphImage("semi", "green", mediaLiderancaSaida)}
                    style={pdfStyles.semiGraph}
                  />
                  <Text style={pdfStyles.graphValue}>{mediaLiderancaSaida.toFixed(1)}</Text>
                </View>
              </View>

              <View style={pdfStyles.graphLine} />
              <Text style={pdfStyles.scoreTitle}>Liderança Score</Text>
            </View>
          </View>
        )}
      </Page>

      {/* Página 2 (explicações) */}
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.upBar} />
        <View style={pdfStyles.mainDiv}>
          <View style={pdfStyles.divTitle}>
            <Image src={imagesBase64.logo} style={pdfStyles.image} />
            <View style={pdfStyles.divTitleAux}>
              <Text style={pdfStyles.title}>Os 5 P's do Protagonismo</Text>
              <Text style={pdfStyles.subtitle}>
                {`São Paulo, ${new Date().toLocaleDateString("pt-BR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}`}
              </Text>
            </View>
          </View>

          <View style={pdfStyles.explanationContainer}>
            {[
              {
                titulo: "Perfil",
                pergunta: "Você se conhece?",
                imagem: imagesBase64.perfil,
                texto:
                  "O protagonista inicia sua jornada pelo autoconhecimento. Compreender suas qualidades, valores e comportamentos permite utilizar suas aptidões de forma estratégica. Reconhecer limitações não significa aceitá-las como barreiras, mas enxergá-las como oportunidades de crescimento. Essa clareza impacta diretamente a comunicação e os relacionamentos, fortalecendo a confiança para agir de acordo com aquilo que realmente faz sentido para você.",
              },
              {
                titulo: "Pessoas",
                pergunta: "Você conhece as pessoas ao seu redor?",
                imagem: imagesBase64.pessoas,
                texto:
                  "O protagonismo se estende além do indivíduo, influenciando positivamente aqueles ao seu redor. No ambiente profissional e pessoal, lidamos com diferentes perfis e opiniões. O desafio é respeitar e valorizar as diferenças, tornando as relações mais produtivas. Relações autênticas exigem escuta ativa, empatia e disposição para compreender o outro. Ao investir tempo em conhecer as pessoas ampliamos nossa visão e fortalecemos a confiança mútua. No ambiente organizacional, essa habilidade melhora a colaboração, evita conflitos e cria uma cultura mais saudável.",
              },
              {
                titulo: "Processo",
                pergunta: "Como os processos impactam seu trabalho?",
                imagem: imagesBase64.processo,
                texto:
                  "Um protagonista busca eficiência nos processos que envolvem seu trabalho. Cada ação faz parte de um processo; alguns fluem naturalmente, enquanto outros são prejudicados por burocracia, falhas na comunicação e falta de clareza. O desafio não é apenas seguir processos, mas aprimorá-los para tornar o trabalho mais eficiente. Diante de atrasos ou falhas, buscar culpados é comum, mas a causa real muitas vezes está em processos mal estruturados. Um olhar crítico ajuda a identificar melhorias e propor soluções eficazes. Compreender os processos conecta sua tarefa ao todo, permitindo decisões mais alinhadas aos objetivos da empresa. Líderes devem garantir processos eficientes, incentivando equipes a questionar, propor mudanças e buscar continuamente a otimização. Processos bem desenhados não engessam, mas criam caminhos para inovação e crescimento.",
              },
              {
                titulo: "Produtividade",
                pergunta: "Como está sua gestão do tempo?",
                imagem: imagesBase64.produtividade,
                texto:
                  "Ser protagonista implica em gerir o tempo de forma eficaz. O tempo é um recurso valioso e desafiador de gerenciar. Você controla sua rotina ou apenas reage às urgências? Produtividade não é fazer mais, mas fazer o que realmente importa. Definir prioridades, manter o foco e evitar a procrastinação otimiza seu desempenho, reduz a sobrecarga mental e melhora resultados sem comprometer sua qualidade de vida. Delegar tarefas também é essencial; assumir tudo sozinho sobrecarrega e reduz a eficiência. Compartilhar responsabilidades permite focar no que agrega mais valor. Além disso, a produtividade exige equilíbrio. Trabalhar sem pausas leva à exaustão. Administrar bem o tempo garante crescimento profissional sem comprometer o bem-estar.",
              },
              {
                titulo: "Propósito",
                pergunta: "O que te move?",
                imagem: imagesBase64.proposito,
                texto:
                  "O protagonista é movido por um propósito claro, que transforma a forma como encara o trabalho e a vida. Quando entendemos por que fazemos o que fazemos, encontramos mais motivação, resiliência e satisfação nas atividades diárias. O propósito está ligado ao sentimento de realização e ao impacto que queremos gerar. Quando esse sentido faz sentido, ele se torna real e nos engaja mesmo diante dos desafios. Ter um propósito não significa ter uma missão grandiosa. Muitas vezes, está nos pequenos detalhes: no impacto positivo, no aprendizado contínuo e na evolução pessoal. Conectados ao propósito, enfrentamos os desafios com mais determinação e fazemos nossas escolhas com mais significado.",
              },
            ].map((item) => (
              <View key={item.titulo} style={pdfStyles.explanationBlock}>
                <Image src={item.imagem} style={pdfStyles.explanationImage} />
                <View style={pdfStyles.explanationTextContainer}>
                  <View style={pdfStyles.explanationTitleRow}>
                    <Image src={imagesBase64.sideImage} style={pdfStyles.explanationIcon} />
                    <View style={pdfStyles.titleDiv}>
                      <Text style={pdfStyles.explanationTitleBold}>{item.titulo}:</Text>
                      <Text style={pdfStyles.explanationTitle}>{item.pergunta}</Text>
                    </View>
                  </View>
                  <Text style={pdfStyles.explanationParagraph}>{item.texto}</Text>
                  <View style={pdfStyles.dividerLine} />
                </View>
              </View>
            ))}
          </View>

          <View style={pdfStyles.noteSection}>
            <View style={pdfStyles.noteBar} />
            <Text style={pdfStyles.noteText}>
              Proibida a reprodução total ou parcial deste relatório, seja qual for o meio,
              eletrônico ou impresso, sem a permissão expressa do{" "}
              <Text style={pdfStyles.noteBold}>Método Santé.</Text>
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PDFDocument;